<template>
    <div
        class="image-load-container w-full h-full relative overflow-hidden"
        :class="containerClass"
    >
        <!-- Loading 占位符：在未加载完成且没有错误时显示 -->
        <div
            v-if="!isLoaded && !hasError"
            class="absolute inset-0 flex items-center justify-center"
            :class="placeholderClass"
        >
            <!-- 自定义 loading 插槽 -->
            <slot v-if="$slots.loading" name="loading"></slot>
            <!-- 默认 loading 动画 -->
            <div v-else class="h-10 w-10">
                <l-dot-spinner
                    v-if="isClient"
                    size="40"
                    speed="0.9"
                    color="var(--el-menu-active-color)"
                ></l-dot-spinner>
            </div>
        </div>

        <!-- 实际内容：始终渲染但通过透明度控制显隐，确保能触发 @load -->
        <div
            class="w-full h-full"
            :class="{ 'opacity-0': !isLoaded, 'opacity-100': isLoaded }"
        >
            <!-- 图片 -->
            <img
                v-if="!isVideo"
                :src="src"
                :alt="alt"
                class="w-full h-full object-cover object-center transition-opacity duration-300"
                :class="imageClass"
                @load="handleLoad"
                @error="handleError"
            />

            <!-- 视频 -->
            <video
                v-else
                :src="src"
                class="w-full h-full object-cover object-center transition-opacity duration-300"
                :class="imageClass"
                muted
                loop
                :autoplay="autoplay"
                @loadeddata="handleLoad"
                @error="handleError"
            />
        </div>

        <!-- 错误状态 -->
        <div
            v-if="hasError"
            class="absolute inset-0 flex items-center justify-center"
            :class="placeholderClass"
        >
            <slot v-if="$slots.error" name="error">
                <div class="text-center text-gray-400">
                    <div class="text-2xl mb-2">📷</div>
                    <div class="text-xs">Load Failed</div>
                </div>
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

interface Props {
    src: string
    alt?: string
    isVideo?: boolean
    autoplay?: boolean
    containerClass?: string
    imageClass?: string
    placeholderClass?: string
}

const props = withDefaults(defineProps<Props>(), {
    alt: '',
    isVideo: false,
    autoplay: true,
    containerClass: '',
    imageClass: '',
    placeholderClass: '',
})

const emit = defineEmits<{
    load: [src: string]
    error: [src: string]
}>()

// 响应式状态
const isLoaded = ref(false)
const hasError = ref(false)

// 处理加载完成
const handleLoad = () => {
    isLoaded.value = true
    hasError.value = false
    emit('load', props.src)
}

// 处理加载错误
const handleError = () => {
    isLoaded.value = false
    hasError.value = true
    emit('error', props.src)
}

// 监听 src 变化，重置状态重新加载
watch(
    () => props.src,
    () => {
        isLoaded.value = false
        hasError.value = false
    }
)

const isClient = ref(false)

onMounted(async () => {
    if (typeof window !== 'undefined') {
        try {
            const { dotSpinner } = await import('ldrs')
            dotSpinner.register()
            isClient.value = true
        } catch (error) {
            console.error('Failed to load ldrs:', error)
            isClient.value = true
        }
    }

    // 如果 src 为空直接标记错误
    if (!props.src) {
        hasError.value = true
    }
})

// 暴露给父组件的方法
defineExpose({
    reload: () => {
        isLoaded.value = false
        hasError.value = false
    },
})
</script>

<style scoped lang="less">
/* 主题色相关样式 */
.primary-border {
    border-color: @tag-selected-bg;
}

.image-load-container {
    transition: all 0.3s ease;
}
</style>
